//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React, { useState } from 'react';
import styled from 'styled-components';

import { CenteredSpinner } from '@components/atoms/spinner/Spinner';
import { BodyErrorText } from '@components/atoms/text/Text';
import Cuid from '@models/Cuid';
import { UIATrackingIds } from '@uia/constants/uiaConstants';
import { useMediaMetadataQuery } from '@uia/details/hooks/useMediaMetadataQuery';
import { UIAFooter } from '@uia/layout/UIAFooter';
import { ProductsContainer } from '@uia/products/ProductsContainer';
import { useProductCatalogsQuery } from '@uia/products/hooks/useProductCatalogsQuery';
import { useProductsQuery } from '@uia/products/hooks/useProductsQuery';
import { isDefined } from '@utils/filterPredicates';

export interface UIAProductsProps {
  cuid: Cuid;
  onClose: () => void;
}

export const UIAProducts = ({ cuid, onClose }: UIAProductsProps) => {
  // TODO: PD-263077 Use setSearchQuery after migration of universal-image-actions-productize-editor
  const [searchQuery, setSearchQuery] = useState('');
  const { data: metadata, isError, isLoading } = useMediaMetadataQuery(cuid);
  const { data: catalogs } = useProductCatalogsQuery();

  // TODO: PD-263077 Use products in the: universal-image-actions-productize-editor
  const { data: products } = useProductsQuery({
    hasCatalog: isDefined(catalogs) && catalogs.length > 0,
    isBvCampaignTagged: metadata?.isBvCampaignTagged,
    keywords: searchQuery,
  });

  return (
    <OverlayWrapper>
      {(() => {
        if (isLoading) {
          return <CenteredSpinner />;
        } else {
          if (isError) {
            return (
              <BodyErrorText>
                An unexpected error occurred. Please try again or contact
                Bazaarvoice Support.
              </BodyErrorText>
            );
          } else {
            return <ProductsContainer cuid={cuid} metadata={metadata} />;
          }
        }
      })()}
      <UIAFooter>
        <UIAFooter.Actions>
          <UIAFooter.ActionButton
            onClick={onClose}
            trackingId={UIATrackingIds.PRODUCTS_CLOSE_MODAL}
          >
            Done
          </UIAFooter.ActionButton>
        </UIAFooter.Actions>
      </UIAFooter>
    </OverlayWrapper>
  );
};

const OverlayWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
  width: 100%;
`;
